import type {
  AnswersMap,
  AnswerValue,
  TestQuestion,
  TestThreshold,
} from "@/types/test";

function maxOptionWeight(q: TestQuestion): number {
  if (q.type !== "single_choice" && q.type !== "multi_choice" && q.type !== "likert") return 0;
  if (q.type === "multi_choice") {
    return q.options.reduce((sum, o) => sum + (o.weight ?? 0), 0);
  }
  return q.options.reduce((max, o) => Math.max(max, o.weight ?? 0), 0);
}

export function computeMaxScore(questions: TestQuestion[]): number {
  return questions.reduce((sum, q) => sum + maxOptionWeight(q), 0);
}

function answeredWeight(q: TestQuestion, answer: AnswerValue | undefined): number {
  if (q.type !== "single_choice" && q.type !== "multi_choice" && q.type !== "likert") return 0;
  if (answer === undefined) return 0;
  const optsByValue = new Map(q.options.map((o) => [o.value, o.weight ?? 0]));
  if (q.type === "multi_choice") {
    if (!Array.isArray(answer)) return 0;
    return answer.reduce((sum, v) => sum + (optsByValue.get(v) ?? 0), 0);
  }
  if (Array.isArray(answer)) return 0;
  return optsByValue.get(answer) ?? 0;
}

function findLabel(score: number, thresholds: TestThreshold[]): string | null {
  for (const t of thresholds) {
    if (score >= t.minScore && score <= t.maxScore) return t.label;
  }
  return null;
}

export interface ScoreResult {
  score: number;
  maxScore: number;
  interpretationLabel: string | null;
}

export function computeScore(
  questions: TestQuestion[],
  thresholds: TestThreshold[],
  answers: AnswersMap,
): ScoreResult | null {
  const maxScore = computeMaxScore(questions);
  if (maxScore === 0) return null;
  const score = questions.reduce(
    (sum, q) => sum + answeredWeight(q, answers[q.id]),
    0,
  );
  return { score, maxScore, interpretationLabel: findLabel(score, thresholds) };
}
